import { useEffect, useState } from "react";
import Script from "next/script";
import { useRouter } from "next/router";
import { useInitialUserEvent } from "hyam-core/hooks/useInitialUserEvent";

/**
 * Iubenda Cookie Solution (cookie banner + consent), loaded directly by the
 * app instead of through a Google Tag Manager tag.
 *
 * Configured with env vars. Renders nothing unless the site id is set:
 *
 *   NEXT_PUBLIC_IUBENDA_SITE_ID=1234567
 *   NEXT_PUBLIC_IUBENDA_COOKIE_POLICY_IDS={"de":"11111111","en":"22222222"}
 *
 * Both values are in the Iubenda embed code of the Cookie Solution
 * (`siteId` and `cookiePolicyId`, one cookie policy per language). A locale
 * without its own id falls back to the first configured language, because
 * Iubenda's free plan only supports one language and renders NO banner for
 * any other.
 *
 * Optional: link the banner to the site's own privacy page instead of the
 * Iubenda-hosted documents (paths are made absolute with the current origin):
 *
 *   NEXT_PUBLIC_IUBENDA_POLICY_URLS={"de":{"privacy":"/datenschutz"},"en":{"privacy":"/en/privacy-policy"}}
 *
 * There is no separate cookie policy page: the privacy policy covers cookies,
 * and the footer's "Cookie Policy" item opens the Iubenda preferences popup
 * (see openCookiePreferences). A `cookie` entry in this variable is ignored.
 *
 * Consent Mode v2: defaults are set to "denied" right away, before GTM starts
 * (even when Iubenda is not configured), and Iubenda updates them once the
 * visitor chooses. GTM tags that respect
 * Consent Mode then only fire after consent.
 *
 * NOTE: the Iubenda tag must NOT also be active inside the GTM container, or
 * the banner will load twice.
 */

const SITE_ID = process.env.NEXT_PUBLIC_IUBENDA_SITE_ID;

const parseCookiePolicyIds = (): Record<string, string> => {
  try {
    return JSON.parse(process.env.NEXT_PUBLIC_IUBENDA_COOKIE_POLICY_IDS || "{}");
  } catch (e) {
    console.error("HyamIubenda: NEXT_PUBLIC_IUBENDA_COOKIE_POLICY_IDS is not valid JSON");
    return {};
  }
};

const COOKIE_POLICY_IDS = parseCookiePolicyIds();

type PolicyUrls = Record<string, { privacy?: string; cookie?: string }>;

const parsePolicyUrls = (): PolicyUrls => {
  try {
    return JSON.parse(process.env.NEXT_PUBLIC_IUBENDA_POLICY_URLS || "{}");
  } catch (e) {
    console.error("HyamIubenda: NEXT_PUBLIC_IUBENDA_POLICY_URLS is not valid JSON");
    return {};
  }
};

const POLICY_URLS = parsePolicyUrls();

const toAbsoluteUrl = (path?: string) =>
  path ? new URL(path, window.location.origin).toString() : undefined;

// Iubenda language codes differ from our locales for English (en-GB).
const IUBENDA_LANG: Record<string, string> = { en: "en-GB" };

const setConsentModeDefaults = () => {
  const w = window as any;

  // React strict mode runs effects twice in dev; only set the defaults once
  if (w.__hyamConsentDefaultsSet) return;
  w.__hyamConsentDefaultsSet = true;

  w.dataLayer = w.dataLayer || [];
  w.gtag =
    w.gtag ||
    function () {
      // gtag expects the raw `arguments` object on the dataLayer
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer.push(arguments);
    };

  w.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
    wait_for_update: 500,
  });
};

export const HyamIubenda = () => {
  const { locale = "en" } = useRouter();
  const [ready, setReady] = useState(false);

  const [shortLocale] = locale.split("-");
  // Iubenda renders no banner at all for a language the plan doesn't include,
  // so a locale without its own policy id falls back to the first configured
  // language (a banner in the wrong language beats no banner).
  const bannerLocale = COOKIE_POLICY_IDS[shortLocale]
    ? shortLocale
    : Object.keys(COOKIE_POLICY_IDS)[0];
  const cookiePolicyId = COOKIE_POLICY_IDS[bannerLocale];

  // Consent defaults must exist before GTM loads, so set them immediately
  useEffect(() => {
    // Always, even without Iubenda configured: tags must never fire before a
    // consent choice, so with no banner they simply stay denied.
    setConsentModeDefaults();
  }, []);

  // Load with the same delay as GTM to protect pagespeed scores
  useEffect(() => {
    const timeoutId = setTimeout(() => setReady(true), 5000);
    return () => clearTimeout(timeoutId);
  }, []);

  useInitialUserEvent(() => setReady(true));

  if (!SITE_ID || !ready) return null;

  if (!cookiePolicyId) {
    console.warn("HyamIubenda: no cookie policy ids configured");
    return null;
  }

  const policyUrls = POLICY_URLS[shortLocale];

  const csConfiguration = {
    siteId: Number(SITE_ID),
    cookiePolicyId: Number(cookiePolicyId),
    lang: IUBENDA_LANG[bannerLocale] || bannerLocale,
    privacyPolicyUrl: toAbsoluteUrl(policyUrls?.privacy),
    // the cookie policy page was removed; the privacy policy covers cookies
    cookiePolicyUrl: toAbsoluteUrl(policyUrls?.privacy),
    countryDetection: true,
    perPurposeConsent: true,
    askConsentAtCookiePolicyUpdate: true,
    enableRemoteConsent: true,
    enableFadp: true,
    // US state privacy laws (CCPA etc.) add a "Notice at collection / Your
    // privacy choices" bar to the bottom of every page. Not needed for a
    // Berlin clinic, so keep it off here and in the Iubenda dashboard.
    enableUspr: false,
    enableLgpd: false,
    whitelabel: false,
    googleConsentMode: true,
    floatingPreferencesButtonDisplay: "bottom-right",
    banner: {
      position: "bottom",
      acceptButtonDisplay: true,
      rejectButtonDisplay: true,
      customizeButtonDisplay: true,
      closeButtonDisplay: false,
      backgroundOverlay: true,
      explicitWithdrawal: true,
      listPurposes: true,
      showTitle: false,
    },
  };

  return (
    <Script
      id="iubenda-cs"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          var _iub = _iub || [];
          _iub.csConfiguration = ${JSON.stringify(csConfiguration)};
          (function () {
            var s = document.createElement("script");
            s.src = "https://cdn.iubenda.com/cs/iubenda_cs.js";
            s.charset = "UTF-8";
            s.async = true;
            document.head.appendChild(s);
          })();
        `,
      }}
    />
  );
};
